import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiService } from '../services/apiService';

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  /** Internal primary key. Never display this or ask the user for it. */
  companyId: string | null;
  /** User-facing "Company ID" (Company.code) — show this in the UI. */
  companyCode?: string | null;
  companyName?: string | null;
  isActive: boolean;
  permissions: string[];
  dataScope: string;
  companyRoleCode: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken?: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: { email: string; password: string; companyId?: string }) => Promise<void>;
  googleLogin: (credentials: { googleId: string; email: string }) => Promise<void>;
  register: (userData: {
    email: string;
    password: string;
    name: string;
    role?: string;
    companyId?: string;
  }) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  loadUserProfile: () => Promise<void>;
  updateProfile: (userData: { name?: string; companyId?: string }) => Promise<void>;
  changePassword: (passwordData: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<void>;
  linkGoogle: (googleData: { googleId: string; email: string }) => Promise<void>;
  unlinkGoogle: () => Promise<void>;
  checkGoogleLinked: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials) => {
        try {
          set({ isLoading: true, error: null });

          const response: any = await apiService.login(credentials);

          if (response.success) {
            const { user, token, refreshToken } = response.data;

            // Ensure permission fields have defaults
            const enrichedUser: User = {
              ...user,
              permissions: user.permissions ?? [],
              dataScope: user.dataScope ?? 'ALL',
              companyRoleCode: user.companyRoleCode ?? '',
            };

            // 儲存 token 到 localStorage
            localStorage.setItem('authToken', token);
            if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

            set({
              user: enrichedUser,
              token,
              refreshToken: refreshToken || null,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
          } else {
            throw new Error(response.error || '登錄失敗');
          }
        } catch (error: any) {
          console.error('Login error:', error);
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || '登錄失敗'
          });
          // 清除可能存在的舊 token
          localStorage.removeItem('authToken');
          throw error;
        }
      },

      googleLogin: async (credentials) => {
        try {
          set({ isLoading: true, error: null });

          const response: any = await apiService.googleLogin(credentials);

          if (response.success) {
            const { user, token, refreshToken } = response.data;

            // Ensure permission fields have defaults
            const enrichedUser: User = {
              ...user,
              permissions: user.permissions ?? [],
              dataScope: user.dataScope ?? 'ALL',
              companyRoleCode: user.companyRoleCode ?? '',
            };

            // 儲存 token 到 localStorage
            localStorage.setItem('authToken', token);
            if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

            set({
              user: enrichedUser,
              token,
              refreshToken: refreshToken || null,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
          } else {
            throw new Error(response.error || 'Google 登錄失敗');
          }
        } catch (error: any) {
          console.error('Google login error:', error);
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || 'Google 登錄失敗'
          });
          localStorage.removeItem('authToken');
          throw error;
        }
      },

      register: async (userData) => {
        try {
          set({ isLoading: true, error: null });

          const response: any = await apiService.register(userData);

          if (response.success) {
            const { user, token } = response.data;

            // Ensure permission fields have defaults
            const enrichedUser: User = {
              ...user,
              permissions: user.permissions ?? [],
              dataScope: user.dataScope ?? 'ALL',
              companyRoleCode: user.companyRoleCode ?? '',
            };

            // 儲存 token 到 localStorage
            localStorage.setItem('authToken', token);

            set({
              user: enrichedUser,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
          } else {
            throw new Error(response.error || '註冊失敗');
          }
        } catch (error: any) {
          console.error('Register error:', error);
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || '註冊失敗'
          });
          localStorage.removeItem('authToken');
          throw error;
        }
      },

      logout: () => {
        // 清除 localStorage 中的 token
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
      },

      clearError: () => {
        set({ error: null });
      },

      loadUserProfile: async () => {
        try {
          const token = localStorage.getItem('authToken');
          const refreshToken = localStorage.getItem('refreshToken');
          if (!token) {
            return;
          }

          set({ isLoading: true });
          
          const response: any = await apiService.getUserProfile();
          
          if (response.success) {
            // Ensure permission fields have defaults
            const enrichedUser: User = {
              ...response.data,
              permissions: response.data.permissions ?? [],
              dataScope: response.data.dataScope ?? 'ALL',
              companyRoleCode: response.data.companyRoleCode ?? '',
            };

            set({
              user: enrichedUser,
              token,
              refreshToken: refreshToken || null,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
          } else {
            throw new Error('獲取用戶資料失敗');
          }
        } catch (error: any) {
          console.error('Load profile error:', error);
          // 保持現有認證狀態，不在此處清 token，避免臨時網絡/服務端錯誤導致誤登出
          set({
            isLoading: false,
          });
        }
      },

      updateProfile: async (userData) => {
        try {
          set({ isLoading: true, error: null });
          
          const response: any = await apiService.updateUserProfile(userData);
          
          if (response.success) {
            set((state) => ({
              user: { ...state.user, ...response.data },
              isLoading: false,
              error: null
            }));
          } else {
            throw new Error(response.error || '更新用戶資料失敗');
          }
        } catch (error: any) {
          console.error('Update profile error:', error);
          set({
            isLoading: false,
            error: error.message || '更新用戶資料失敗'
          });
          throw error;
        }
      },

      changePassword: async (passwordData) => {
        try {
          set({ isLoading: true, error: null });

          const response: any = await apiService.changePassword(passwordData);

          if (response.success) {
            set({
              isLoading: false,
              error: null
            });
          } else {
            throw new Error(response.error || '修改密碼失敗');
          }
        } catch (error: any) {
          console.error('Change password error:', error);
          set({
            isLoading: false,
            error: error.message || '修改密碼失敗'
          });
          throw error;
        }
      },

      linkGoogle: async (googleData) => {
        try {
          set({ isLoading: true, error: null });

          const response: any = await apiService.linkGoogle(googleData);

          if (response.success) {
            set({ isLoading: false, error: null });
          } else {
            throw new Error(response.error || '綁定 Google 帳戶失敗');
          }
        } catch (error: any) {
          console.error('Link Google error:', error);
          set({
            isLoading: false,
            error: error.message || '綁定 Google 帳戶失敗'
          });
          throw error;
        }
      },

      unlinkGoogle: async () => {
        try {
          set({ isLoading: true, error: null });

          const response: any = await apiService.unlinkGoogle();

          if (response.success) {
            set({ isLoading: false, error: null });
          } else {
            throw new Error(response.error || '解除綁定失敗');
          }
        } catch (error: any) {
          console.error('Unlink Google error:', error);
          set({
            isLoading: false,
            error: error.message || '解除綁定失敗'
          });
          throw error;
        }
      },

      checkGoogleLinked: async () => {
        try {
          const response: any = await apiService.checkGoogleLinked();
          return response.success ? response.data.isLinked : false;
        } catch (error: any) {
          console.error('Check Google linked error:', error);
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
